import { useEffect } from 'react';
import { getCommands } from '@/lib/commands';
import { useUiStore } from '@/stores/uiStore';

function matchesShortcut(event: KeyboardEvent, shortcut: string): boolean {
  const parts = shortcut
    .toLowerCase()
    .split('+')
    .map((part) => part.trim());
  const key = parts[parts.length - 1];
  const ctrlOrMeta = event.ctrlKey || event.metaKey;

  return (
    event.key.toLowerCase() === key &&
    ctrlOrMeta === parts.includes('ctrl') &&
    event.altKey === parts.includes('alt') &&
    event.shiftKey === parts.includes('shift')
  );
}

/**
 * Registers the app-wide shortcuts once at the shell root: Ctrl/Cmd+K for the
 * command palette, Ctrl/Cmd+P to go to a panel, and whatever else the command
 * registry declares a `shortcut` for — so a shortcut can never fire a command
 * the palette doesn't also expose, and vice versa.
 */
export function useKeyboardShortcuts(): void {
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      const ctrlOrMeta = event.ctrlKey || event.metaKey;
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.tagName === 'SELECT' ||
        target?.isContentEditable === true;

      if (!isTyping && !ctrlOrMeta && !event.altKey && event.key === '?') {
        event.preventDefault();
        useUiStore.getState().openEducationCenter();
        return;
      }

      if (ctrlOrMeta && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        useUiStore.getState().openPalette('commands');
        return;
      }
      if (ctrlOrMeta && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        useUiStore.getState().openPalette('panels');
        return;
      }

      for (const command of getCommands()) {
        if (command.shortcut && matchesShortcut(event, command.shortcut)) {
          event.preventDefault();
          command.run();
          return;
        }
      }
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
}
